<script setup>
import { cn } from '@/shadcn/lib/utils'
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits
} from 'reka-ui'
import { computed } from 'vue'
import { useDialogContent } from './useDialogContent.js'

const props = defineProps({
  forceMount: { type: Boolean, required: false },
  trapFocus: { type: Boolean, required: false },
  disableOutsidePointerEvents: { type: Boolean, required: false },
  asChild: { type: Boolean, required: false },
  as: { type: null, required: false },
  class: { type: null, required: false },
  disablePortal: { type: Boolean, required: false },
  canClose: { type: Boolean, default: true },
  isStaticBackdrop: { type: Boolean, default: false }
})
const emits = defineEmits([
  'escapeKeyDown',
  'pointerDownOutside',
  'focusOutside',
  'interactOutside',
  'openAutoFocus',
  'closeAutoFocus'
])

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)

const { onInteractOutside, onOpenAutoFocus } = useDialogContent(props)

</script>

<template>
  <DialogPortal
    :disabled="disablePortal"
  >
    <DialogOverlay
      class="tw-fixed tw-inset-0 tw-z-[96] tw-bg-black/20 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0"
    />
    <DialogContent
      v-bind="forwarded"
      :class="
        cn(
          'focus-visible:tw-outline-none tw-rounded-lg tw-py-6 tw-fixed tw-left-1/2 tw-top-1/2 tw-z-[100] tw-grid tw-w-11/12 tw-max-w-lg tw--translate-x-1/2 tw--translate-y-1/2 tw-gap-4 tw-border tw-border-border/20 tw-bg-background tw-shadow-lg tw-duration-200 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[state=closed]:tw-slide-out-to-left-1/2 data-[state=closed]:tw-slide-out-to-top-[48%] data-[state=open]:tw-slide-in-from-left-1/2 data-[state=open]:tw-slide-in-from-top-[48%] md:tw-w-full',
          props.class,
        )
      "
      :aria-describedby="undefined"
      @interact-outside="onInteractOutside"
      @open-auto-focus="onOpenAutoFocus"
    >
      <slot />

      <DialogClose
        v-if="canClose"
        class="tw-absolute tw-leading-none tw-right-4 tw-top-4 tw-rounded-sm tw-opacity-70 tw-ring-offset-background tw-transition-opacity hover:tw-opacity-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-pointer-events-none data-[state=open]:tw-bg-accent data-[state=open]:tw-text-muted-foreground"
      >
        <i class="tw-i-fal-xmark tw-w-4 tw-h-4" />
        <span class="tw-sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
